import {noop} from 'lodash';
import React, {FC, useContext} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {styled} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {usePush} from '../../../../../srcCommon/push/pushContext';
import {buildTeammateDisplayName, buildTeammateInboxPath} from '../../../../helpers/modelHelpers';
import {DelegationModel} from '../../../../models/api/delegationModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {TeammateOnlineStatusesEnum} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {InboxesPanelId} from '../../../../store/panels/panelStateBase';
import {LeftPanelContext} from '../../../app/leftPanelContext';
import {useWorkspaceDragAndDrop} from '../dnd/workspaceDragDropHooks';
import {WorkspaceDragItem, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {WorkspaceHeader} from '../header/workspaceHeader';
import {Workspace} from '../workspace';
import {WorkspaceTeammateItemContainerBase} from '../workspaceTeammateItemContainerBase';
import {DelegationWorkspaceCustomViewsContainer} from './delegationWorkspaceCustomViewsContainer';
import {DelegationWorkspaceInboxes} from './delegationWorkspaceInboxes';
import {DelegationWorkspaceInboxTagContainer} from './delegationWorkspaceInboxTagContainer';

/*
 * Props.
 */

export interface DelegationWorkspaceProps {
  panelId: InboxesPanelId;
  delegation: DelegationModel;
  owner: TeammateModel;
  ownerInboxes: ReadonlyArray<InboxModel>;
  ownerOnlineStatus: TeammateOnlineStatusesEnum;
  isExpanded: boolean;
  onToggleExpandClick: Handler;
  workspaceDragItem?: WorkspaceDragItem;
  onWorkspaceItemDrop?: WorkspaceItemDropHandler;
  shouldAlphabetizeWorkspace: boolean;
}

/*
 * Style.
 */

const StyledDelegatedSpan = styled.span`
  font-weight: ${fontWeights.light};
`;

const StyledPreviewDiv = styled.div`
  position: absolute;
  z-index: -1;
  pointer-events: none;
  background: ${(p) => p.theme.background};
  border-radius: 4px;
`;

/*
 * Component.
 */

export const DelegationWorkspace: FC<DelegationWorkspaceProps> = (props) => {
  const {panelId, owner, isExpanded, onToggleExpandClick} = props;
  const {scrollerElement} = useContext(LeftPanelContext);
  const {isConnected} = usePush();
  const isOffline = !isConnected;
  const {dndRef, dropLinePreviewRef, dragPreviewRef} = useWorkspaceDragAndDrop({
    scrollerElement,
    workspaceDragItem: props.workspaceDragItem,
    onWorkspaceItemDrop: props.onWorkspaceItemDrop,
    shouldAlphabetizeWorkspace: props.shouldAlphabetizeWorkspace,
  });

  return (
    <Workspace isExpanded={isExpanded} ref={dropLinePreviewRef}>
      {/* Header. */}
      <WorkspaceHeader
        panelId={panelId}
        ref={dndRef}
        interactionId="delegation"
        isExpanded={isExpanded}
        onToggleExpandClick={onToggleExpandClick}
        isOffline={isOffline}
      >
        {renderTitle(owner)}
      </WorkspaceHeader>
      <StyledPreviewDiv
        ref={(el) =>
          dragPreviewRef(el, {
            offsetX: 1,
            offsetY: 1,
          })
        }
      >
        <WorkspaceHeader panelId={panelId} interactionId="draggable-delegation" isOffline={isOffline}>
          {renderTitle(owner)}
        </WorkspaceHeader>
      </StyledPreviewDiv>

      {/* Workspace content. */}
      {maybeRenderExpandedContent(props, isOffline)}
    </Workspace>
  );
};

function renderTitle(owner: TeammateModel) {
  return (
    <>
      {buildTeammateDisplayName(owner)}
      <StyledDelegatedSpan>
        &nbsp;(<Trans id="workspace-delegation-header">delegated</Trans>)
      </StyledDelegatedSpan>
    </>
  );
}

function maybeRenderExpandedContent(props: DelegationWorkspaceProps, isOffline: boolean) {
  const {isExpanded, delegation} = props;
  if (!isExpanded) {
    return null;
  }

  return delegation.isFull
    ? renderFullDelegation(props, isOffline)
    : renderPartialDelegation(props, isOffline);
}

function renderFullDelegation(props: DelegationWorkspaceProps, isOffline: boolean) {
  const {panelId, delegation, owner, ownerInboxes, ownerOnlineStatus} = props;
  return (
    <>
      {/* Inbox. */}
      <DelegationWorkspaceInboxTagContainer
        panelId={panelId}
        delegation={delegation}
        owner={owner}
        ownerInboxes={ownerInboxes}
        ownerOnlineStatus={ownerOnlineStatus}
      />

      {/* Custom views */}
      <DelegationWorkspaceCustomViewsContainer
        panelId={panelId}
        delegation={delegation}
        shouldAlphabetizeWorkspace={false}
      />
    </>
  );
}

function renderPartialDelegation(props: DelegationWorkspaceProps, isOffline: boolean) {
  const {panelId, delegation, owner, ownerOnlineStatus, ownerInboxes} = props;

  // Delegation of 1 inbox: render a single item.
  if (ownerInboxes.length === 1) {
    const inbox = ownerInboxes[0];
    return (
      <WorkspaceTeammateItemContainerBase
        panelId={panelId}
        isDisabled={isOffline}
        teammate={owner}
        onlineStatus={ownerOnlineStatus}
        to={buildTeammateInboxPath(inbox.id, InboxesFiltersEnum.OPEN)}
        shouldHideMoreMenu
        canBeHidden={false}
        shouldAlphabetizeWorkspace={false}
        workspaceDragItem={undefined}
        isEditingWorkspace={false}
        onDropReorderTeammate={noop}
      >
        {inbox.name}
      </WorkspaceTeammateItemContainerBase>
    );
  }

  return (
    <DelegationWorkspaceInboxes
      panelId={panelId}
      delegation={delegation}
      inboxes={ownerInboxes}
      isDisabled={isOffline}
    />
  );
}
